import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Axios from 'axios';
import './index.scss';

import { apiHost, sourceHost } from '../../utils/Shared/constant';
import PageSelector from '../../utils/PageSelector';

// API:
//   - GET /api/WeakIndex/GetWeakIndicatorTree   → 指標樹（filterCategories）
//   - GET /api/Content/AvailableCaseStoryList   → 案例故事清單（stories）

const STORY_PER_PAGE = 9;
const DEFAULT_STORY_IMAGE = '/banner_img/morakot-en_household-demographics.png';

const SearchIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" focusable="false">
		<path d="M17.7801 16.7203L13.3033 12.2436C14.5233 10.7515 15.1231 8.84764 14.9787 6.92574C14.8343 5.00384 13.9567 3.21097 12.5275 1.91796C11.0983 0.624952 9.22678 -0.0692619 7.30007 -0.0210881C5.37336 0.0270858 3.53886 0.813961 2.17605 2.17678C0.813229 3.5396 0.0263534 5.37409 -0.0218205 7.3008C-0.0699943 9.22751 0.624219 11.099 1.91723 12.5283C3.21023 13.9575 5.00311 14.8351 6.92501 14.9795C8.84691 15.1239 10.7508 14.524 12.2428 13.3041L16.7196 17.7808C16.861 17.9174 17.0505 17.993 17.2471 17.9913C17.4438 17.9896 17.6319 17.9107 17.771 17.7717C17.91 17.6326 17.9889 17.4445 17.9906 17.2479C17.9923 17.0512 17.9167 16.8618 17.7801 16.7203ZM7.49984 13.5006C6.31315 13.5006 5.15311 13.1487 4.16642 12.4894C3.17972 11.8301 2.41069 10.893 1.95656 9.79667C1.50243 8.70031 1.38361 7.49391 1.61512 6.33003C1.84664 5.16614 2.41808 4.09704 3.2572 3.25793C4.09631 2.41881 5.16541 1.84737 6.3293 1.61586C7.49318 1.38435 8.69958 1.50317 9.79594 1.95729C10.8923 2.41142 11.8294 3.18045 12.4887 4.16715C13.1479 5.15384 13.4998 6.31388 13.4998 7.50057C13.4981 9.09132 12.8653 10.6164 11.7405 11.7412C10.6157 12.8661 9.09059 13.4988 7.49984 13.5006Z" fill="currentColor"/>
	</svg>
);

// 案例故事清單元件（自管 state、無 props、無 Redux）。
// 可被任何 consumer 直接 mount，內部完成搜尋 / 篩選 / 分頁 / API 呼叫。
class SviCaseStoryList extends Component {
	constructor(params) {
		super(params);
		this.state = {
			searchKeyword: '',
			currentPage: 1,
			checkedIndicators: {},
			filterCategories: [],
			indicatorMap: {},
			stories: [],
			totalPages: 1,
		};
	}

	componentDidMount() {
		this.loadFilterCategories();
		this.loadStories();
	}

	componentDidUpdate(prevProps, prevState) {
		if (
			prevState.checkedIndicators !== this.state.checkedIndicators ||
			prevState.currentPage !== this.state.currentPage
		) {
			this.loadStories();
		}
	}

	loadFilterCategories = () => {
		Axios.get(`${apiHost}/api/WeakIndex/GetWeakIndicatorTree`)
			.then((res) => {
				if (res.status !== 200 || !res.data || !res.data.Result) return;
				const { categories, subcategories, indicators } = res.data.Result;
				const tree = (categories || []).map(cat => ({
					name: cat.name,
					categoryIndicatorId: cat.indicator_id,
					subcategories: (subcategories || [])
						.filter(sub => sub.category_id === cat.category_id)
						.map(sub => ({
							name: sub.name,
							subcategoryId: sub.subcategory_id,
							subcategoryIndicatorId: sub.indicator_id,
							indicators: (indicators || [])
								.filter(ind =>
									ind.category_id === cat.category_id &&
									ind.subcategory_id === sub.subcategory_id
								)
								.map(ind => ({ id: ind.indicator_id, name: ind.name })),
						})),
				}));
				const indicatorMap = {};
				(indicators || []).forEach(ind => {
					const cat = (categories || []).find(c => c.category_id === ind.category_id);
					const sub = (subcategories || []).find(s =>
						s.category_id === ind.category_id && s.subcategory_id === ind.subcategory_id
					);
					indicatorMap[ind.indicator_id] = {
						categoryName: cat ? cat.name : '',
						subcategoryName: sub ? sub.name : '',
						name: ind.name,
					};
				});
				this.setState({ filterCategories: tree, indicatorMap });
			})
			.catch((err) => {
				console.error('GetWeakIndicatorTree failed:', err);
			});
	}

	// 依使用者勾選狀態，計算最終要送後端的 IndicatorIds：
	//   - 所有被勾選的末端指標 id（level=3）
	//   - 某主分類下末端全部被勾 → 補上該分類的 level=1 id
	//   - 某次分類下末端全部被勾 → 補上該次分類的 level=2 id
	getEffectiveIndicatorIds = () => {
		const ids = new Set();
		this.state.filterCategories.forEach(mainCat => {
			const subResults = mainCat.subcategories.map(sub => {
				const checkedLeaves = sub.indicators.filter(ind => this.state.checkedIndicators[ind.id]);
				checkedLeaves.forEach(ind => ids.add(ind.id));
				const allSubLeavesChecked = sub.indicators.length > 0 && checkedLeaves.length === sub.indicators.length;
				if (allSubLeavesChecked && sub.subcategoryIndicatorId) {
					ids.add(sub.subcategoryIndicatorId);
				}
				return allSubLeavesChecked;
			});
			const allSubsFullChecked = mainCat.subcategories.length > 0 && subResults.every(v => v);
			if (allSubsFullChecked && mainCat.categoryIndicatorId) {
				ids.add(mainCat.categoryIndicatorId);
			}
		});
		return Array.from(ids);
	}

	loadStories = () => {
		const effectiveIds = this.getEffectiveIndicatorIds();
		const params = new URLSearchParams();
		params.append('CurrentPage', this.state.currentPage);
		params.append('PerPage', STORY_PER_PAGE);
		if (this.state.searchKeyword) {
			params.append('KeyWord', this.state.searchKeyword);
		}
		effectiveIds.forEach(id => params.append('IndicatorIds', id));
		Axios.get(`${apiHost}/api/Content/AvailableCaseStoryList?${params.toString()}`)
			.then((res) => {
				if (res.status !== 200 || !res.data) return;
				const list = res.data.Result || [];
				const totalPages = res.data.TotalPage || 1;
				this.setState({ stories: list, totalPages });
			})
			.catch((err) => {
				console.error('AvailableCaseStoryList failed:', err);
				this.setState({ stories: [], totalPages: 1 });
			});
	}

	handleSearchChange = (e) => {
		this.setState({ searchKeyword: e.target.value });
	}

	handleSearchSubmit = () => {
		this.setState({ currentPage: 1 }, () => this.loadStories());
	}

	handleSearchKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			this.handleSearchSubmit();
		}
	}

	handlePageClick = (page) => {
		if (page < 1) return;
		this.setState({ currentPage: page });
	}

	toggleIndicator = (id) => {
		this.setState(prev => ({
			checkedIndicators: {
				...prev.checkedIndicators,
				[id]: !prev.checkedIndicators[id],
			},
			currentPage: 1,
		}));
	}

	toggleSubcategory = (sub) => {
		this.setState(prev => {
			const next = { ...prev.checkedIndicators };
			const allChecked = sub.indicators.every(ind => next[ind.id]);
			sub.indicators.forEach(ind => {
				next[ind.id] = !allChecked;
			});
			return { checkedIndicators: next, currentPage: 1 };
		});
	}

	toggleMainCategory = (mainCat) => {
		this.setState(prev => {
			const next = { ...prev.checkedIndicators };
			const allIds = [];
			mainCat.subcategories.forEach(sub => {
				sub.indicators.forEach(ind => { allIds.push(ind.id); });
			});
			const allChecked = allIds.every(id => next[id]);
			allIds.forEach(id => { next[id] = !allChecked; });
			return { checkedIndicators: next, currentPage: 1 };
		});
	}

	clearAllFilters = () => {
		this.setState({ checkedIndicators: {}, currentPage: 1 });
	}

	isSubcategoryChecked = (sub) => {
		return sub.indicators.length > 0 && sub.indicators.every(ind => this.state.checkedIndicators[ind.id]);
	}

	isMainCategoryChecked = (mainCat) => {
		const allIds = [];
		mainCat.subcategories.forEach(sub => {
			sub.indicators.forEach(ind => { allIds.push(ind.id); });
		});
		return allIds.length > 0 && allIds.every(id => this.state.checkedIndicators[id]);
	}

	renderStoryCard = (story) => {
		const fallback = story.IndicatorId ? this.state.indicatorMap[story.IndicatorId] : null;
		const categoryName = story.IndicatorCategoryName || (fallback && fallback.categoryName);
		const subcategoryName = story.IndicatorSubcategoryName || (fallback && fallback.subcategoryName);
		const indicatorName = story.IndicatorName || (fallback && fallback.name);
		const level = story.IndicatorLevel || 0;
		const tags = [];
		if (categoryName) tags.push({ text: categoryName, variant: 'outline' });
		if (level >= 2 && subcategoryName) tags.push({ text: subcategoryName, variant: 'outline' });
		if (level >= 3 && indicatorName) tags.push({ text: indicatorName, variant: 'outline' });
		if (tags.length > 0) tags[tags.length - 1].variant = 'filled';
		const imgPath = story.ImageUrl ? `${sourceHost}/${story.ImageUrl}` : DEFAULT_STORY_IMAGE;
		return (
			<Link className="story-item" to={{ pathname: `/evaluation/svi/story/${story.ID}`, state: { from: window.location.pathname + window.location.search } }} key={story.ID} aria-label="閱讀案例故事文章">
				<article className="story-card">
					<div className="story-card__image">
						<img src={imgPath} alt="案例故事文章示意圖" className="story-card__img" />
					</div>
					<div className="story-card__tags">
						{tags.map((tag, i) => (
							<span key={i} className={`story-card__tag story-card__tag--${tag.variant}`}>{tag.text}</span>
						))}
					</div>
					<h3 className="story-card__title">{story.Title}</h3>
					<p className="story-card__summary">{story.Summary}</p>
					<time className="story-card__date" dateTime={story.Published}>{story.Published}</time>
				</article>
			</Link>
		);
	}

	render() {
		const { currentPage, stories, totalPages } = this.state;
		return (
			<div className="svi-case-story-list">
				<div className="story-search-block">
					<div className="story-search-block-inner">
						<div className="d-flex flex-wrap justify-content-between align-items-center w-100">
							<div className="story-search" role="search">
								<input
									type="search"
									className="story-search-input"
									placeholder="請輸入關鍵字 搜尋相關文章"
									aria-label="搜尋相關文章"
									autoComplete="off"
									value={this.state.searchKeyword}
									onChange={this.handleSearchChange}
									onKeyDown={this.handleSearchKeyDown}
								/>
								<button
									type="button"
									className="story-search-icon"
									aria-label="搜尋"
									onClick={this.handleSearchSubmit}
									style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
								>
									<SearchIcon />
								</button>
							</div>
						</div>
					</div>
				</div>
				{/* 展開篩選面板（Bootstrap 4 collapse） */}
				<div className="story-filter">
					<button
						className="story-filter-button collapsed"
						type="button"
						data-toggle="collapse"
						data-target="#collapseTypeOrIndicator"
						aria-expanded="false"
						aria-controls="collapseTypeOrIndicator"
					>
						<div className="story-filter-button-text">請選擇分類或指標</div>
						<div className="story-filter-button-toggle">
							<svg className="icon" xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
								<path d="M6.14471e-06 12.162C-0.000566056 12.0688 0.0183529 11.9764 0.0556759 11.8901C0.0929989 11.8038 0.147995 11.7253 0.217505 11.6591L6.345 5.87204C6.69334 5.54222 7.10716 5.28054 7.56274 5.10199C8.01833 4.92345 8.50674 4.83154 9 4.83154C9.49325 4.83154 9.98166 4.92345 10.4372 5.10199C10.8928 5.28054 11.3066 5.54222 11.655 5.87204L17.7825 11.6591C17.8524 11.7252 17.9079 11.8036 17.9457 11.8899C17.9836 11.9762 18.0031 12.0686 18.0031 12.162C18.0031 12.2554 17.9836 12.3479 17.9457 12.4342C17.9079 12.5205 17.8524 12.5989 17.7825 12.665L10.59 6.87787C10.1681 6.47993 9.59625 6.25641 8.99999 6.25641C8.40374 6.25641 7.83187 6.47993 7.41 6.87787L1.2825 12.665C1.21278 12.7313 1.12983 12.784 1.03844 12.82C0.947043 12.856 0.849013 12.8745 0.750004 12.8745C0.650996 12.8745 0.552966 12.856 0.461571 12.82C0.370179 12.784 0.287228 12.7313 0.217505 12.665C0.147995 12.5988 0.0929989 12.5203 0.0556759 12.434C0.0183529 12.3477 -0.000566064 12.2553 6.14471e-06 12.162Z" fill="#7CA9FE"/>
							</svg>
						</div>
					</button>
					<div className="collapse" id="collapseTypeOrIndicator">
						<div className="story-filter-collapse-inner">
							<a className="btnClear" href="#" onClick={(e) => { e.preventDefault(); this.clearAllFilters(); }}>清除所選項目</a>
							<div className="story-filter-content">
								{this.state.filterCategories.map((mainCat, mi) => {
									const collapseId = `collapseFilterCat${mi}`;
									const showByDefault = mi === 0;
									return (
									<div className="type-block" key={mi}>
										<div className="main-type-row">
											<div className="form-check main-type">
												<input
													className="form-check-input"
													type="checkbox"
													id={`filter-main-${mi}`}
													checked={this.isMainCategoryChecked(mainCat)}
													onChange={() => this.toggleMainCategory(mainCat)}
												/>
												<label className="form-check-label" htmlFor={`filter-main-${mi}`}>{mainCat.name}</label>
											</div>
											<button
												type="button"
												className="story-filter-collapse-btn d-lg-none"
												data-toggle="collapse"
												data-target={`#${collapseId}`}
												aria-expanded={showByDefault}
												aria-controls={collapseId}
												aria-label={`展開或收合${mainCat.name}`}
											>
												<svg className="story-filter-collapse-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 17" fill="none" aria-hidden="true">
													<path d="M6.14471e-06 12.162C-0.000566056 12.0688 0.0183529 11.9764 0.0556759 11.8901C0.0929989 11.8038 0.147995 11.7253 0.217505 11.6591L6.345 5.87204C6.69334 5.54222 7.10716 5.28054 7.56274 5.10199C8.01833 4.92345 8.50674 4.83154 9 4.83154C9.49325 4.83154 9.98166 4.92345 10.4372 5.10199C10.8928 5.28054 11.3066 5.54222 11.655 5.87204L17.7825 11.6591C17.8524 11.7252 17.9079 11.8036 17.9457 11.8899C17.9836 11.9762 18.0031 12.0686 18.0031 12.162C18.0031 12.2554 17.9836 12.3479 17.9457 12.4342C17.9079 12.5205 17.8524 12.5989 17.7825 12.665L10.59 6.87787C10.1681 6.47993 9.59625 6.25641 8.99999 6.25641C8.40374 6.25641 7.83187 6.47993 7.41 6.87787L1.2825 12.665C1.21278 12.7313 1.12983 12.784 1.03844 12.82C0.947043 12.856 0.849013 12.8745 0.750004 12.8745C0.650996 12.8745 0.552966 12.856 0.461571 12.82C0.370179 12.784 0.287228 12.7313 0.217505 12.665C0.147995 12.5988 0.0929989 12.5203 0.0556759 12.434C0.0183529 12.3477 -0.000566064 12.2553 6.14471e-06 12.162Z" fill="#7CA9FE"/>
												</svg>
											</button>
										</div>
										<div className={`collapse${showByDefault ? ' show' : ''}`} id={collapseId}>
											{mainCat.subcategories.map((sub, si) => (
												<div className="sub-type" key={si}>
													<div className="form-check sub-type-title">
														<input
															className="form-check-input"
															type="checkbox"
															id={`filter-${mi}-sub-${si}`}
															checked={this.isSubcategoryChecked(sub)}
															onChange={() => this.toggleSubcategory(sub)}
														/>
														<label className="form-check-label" htmlFor={`filter-${mi}-sub-${si}`}>{sub.name}</label>
													</div>
													{sub.indicators.map((ind) => {
														const inputId = `filter-ind-${ind.id}`;
														return (
															<div className="form-check" key={ind.id}>
																<input
																	className="form-check-input"
																	type="checkbox"
																	id={inputId}
																	checked={!!this.state.checkedIndicators[ind.id]}
																	onChange={() => this.toggleIndicator(ind.id)}
																/>
																<label className="form-check-label" htmlFor={inputId}>{ind.name}</label>
															</div>
														);
													})}
												</div>
											))}
										</div>
									</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>
				<div className="container story-wrap">
					{this.props.pavilionDropdown}
					<div className="story-list">
						{stories.map(story => this.renderStoryCard(story))}
					</div>
					<PageSelector className="pagination" nowPage={currentPage} maxPage={totalPages} onPageClick={(page) => this.handlePageClick(page)} />
				</div>
			</div>
		);
	}
}

export default SviCaseStoryList;
